<template>
  <div class="page-shell">
    <CCard class="border-0 shadow-sm page-card">
      <CCardHeader class="d-flex flex-wrap justify-content-between align-items-center page-header">
        <div class="mr-3 mb-2 mb-md-0">
          <div class="text-uppercase small eyebrow">CRM & Calls</div>
          <strong class="h4 d-block mb-1">MDI Feedback Call</strong>
          <div class="small text-muted">Daily feedback tracker with export support</div>
        </div>
        <div class="d-flex flex-wrap align-items-end tools-wrap">
          <CInput
            class="mr-md-2 mb-2 mb-md-0"
            label="Order Date"
            type="date"
            v-model="orderDate"
          />
          <CButton color="primary" class="mr-2 mb-2 mb-md-0" :disabled="isLoading" @click="searchRows">
            {{ isLoading ? 'Searching...' : 'Search' }}
          </CButton>
          <CButton color="success" variant="outline" :disabled="isDownloading" class="mb-2 mb-md-0" @click="downloadRows">
            {{ isDownloading ? 'Preparing...' : 'Export' }}
          </CButton>
        </div>
      </CCardHeader>

      <CCardBody>
        <CAlert v-if="errorMessage" color="danger" show>
          {{ errorMessage }}
        </CAlert>

        <div v-if="isLoading" class="text-center py-5 text-muted">Loading MDI feedback calls...</div>

        <div v-else-if="!rows.length" class="empty-state text-center py-5">
          <div class="h5 mb-2">No items</div>
          <div class="text-muted">Try a different date.</div>
        </div>

        <div v-else class="table-responsive table-wrap">
          <table class="table table-hover mb-0 table-sm">
            <thead>
              <tr>
                <th>Order Number</th>
                <th>Contact Name</th>
                <th>Contact Phone</th>
                <th>Order Date</th>
                <th>Medicine</th>
                <th>Last Call Date</th>
                <th>Last Session Date</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in rows" :key="row.order_number + '-' + index">
                <td>{{ row.order_number || '-' }}</td>
                <td>{{ row.contact_name || '-' }}</td>
                <td>{{ row.contact_phone || '-' }}</td>
                <td>{{ row.order_date || '-' }}</td>
                <td class="wide-cell">{{ row.medicine || '-' }}</td>
                <td>{{ row.last_call_date || '-' }}</td>
                <td>{{ row.last_session_date || '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import axios from 'axios'

function isoToday () {
  const d = new Date()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

export default {
  name: 'MDIFeedbackCall',
  data () {
    return {
      orderDate: isoToday(),
      rows: [],
      isLoading: false,
      isDownloading: false,
      errorMessage: ''
    }
  },
  created () {
    this.searchRows()
  },
  methods: {
    requestPayload (type) {
      return {
        order_date: this.orderDate,
        type
      }
    },
    apiUrl () {
      return `${this.$apiAdress}/api/calls_queue/mdi_feedback_calls_list?token=${localStorage.getItem('api_token')}`
    },
    normalizeRows (input) {
      if (!Array.isArray(input)) return []
      return input.map((item) => ({
        order_number: item?.order_number || '',
        contact_name: item?.contact_name || '',
        contact_phone: item?.contact_phone || '',
        order_date: item?.order_date || '',
        medicine: item?.medicine || '',
        last_call_date: item?.last_call_date || '',
        last_session_date: item?.last_session_date || ''
      }))
    },
    parseError (error, fallback) {
      const validationErrors = error?.response?.data?.error
      if (validationErrors && typeof validationErrors === 'object') {
        const key = Object.keys(validationErrors)[0]
        if (key && Array.isArray(validationErrors[key]) && validationErrors[key][0]) {
          return validationErrors[key][0]
        }
      }
      return error?.response?.data?.message || fallback
    },
    searchRows () {
      this.errorMessage = ''
      this.isLoading = true
      axios.post(this.apiUrl(), this.requestPayload('search'))
        .then((response) => {
          this.rows = this.normalizeRows(response.data)
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Unable to fetch MDI feedback calls.')
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    downloadRows () {
      this.errorMessage = ''
      this.isDownloading = true

      axios({
        method: 'post',
        url: this.apiUrl(),
        data: this.requestPayload('download'),
        responseType: 'arraybuffer'
      })
        .then((response) => {
          const contentDisposition = response.headers['content-disposition'] || ''
          const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i)
          const fileName = filenameMatch && filenameMatch[1] ? filenameMatch[1] : 'feedbackcalls.csv'

          const blobUrl = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          link.href = blobUrl
          link.setAttribute('download', fileName)
          document.body.appendChild(link)
          link.click()
          link.remove()
          window.URL.revokeObjectURL(blobUrl)
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Unable to export MDI feedback calls.')
        })
        .finally(() => {
          this.isDownloading = false
        })
    }
  }
}
</script>

<style scoped>
.page-shell {
  background: linear-gradient(180deg, #f5f8fd 0%, #eef3fa 100%);
}

.page-card {
  border-radius: 16px;
}

.page-header {
  background: linear-gradient(135deg, #ffffff 0%, #f6f9ff 100%);
}

.eyebrow {
  letter-spacing: 0.08em;
  color: #66748a;
  font-weight: 700;
}

.tools-wrap {
  gap: 0.5rem;
}

.table-wrap {
  border: 1px solid #e7edf5;
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
}

.table-wrap thead th {
  background: #f8fbff;
  color: #4a5872;
  font-size: 0.76rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  white-space: nowrap;
}

.wide-cell {
  min-width: 240px;
  white-space: normal;
}

.empty-state {
  border: 1px dashed #d7dfeb;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.72);
}
</style>
